import WRAP from "WRAP-JS";
import { deepCopy, formatLonLatATC1 } from "../../../utils/FPEUtils";
import { configFlightHistoryDh, configFlightHistoryTrackLineLayer, configFlightHistoryTrackPointLayer } from "./config/ConfigFlightHistory";
import { MAP_ID_FLIGHT_HISTORY } from "../../../store/MapContentsState";
import LegendFlightHistory from "../legend/LegendFlightHistory";

class FlightHistory {
  static NAME = MAP_ID_FLIGHT_HISTORY;
  TRACK_POINT = `${MAP_ID_FLIGHT_HISTORY}_TRACK_POINT`;
  TRACK_LINE = `${MAP_ID_FLIGHT_HISTORY}_TRACK_LINE`;

  visible;
  parameter = {};
  orgData = {};
  ready = false;

  flightHistoryDh;
  trackPointLayer;
  trackLineLayer;

  LABEL_OFFSET_X = 10;
  LABEL_OFFSET_Y = 10;
  LABEL_PADDING = 2;
  labelFontSize = 12;
  labelFillStyle = "#F9CB9C";
  labelFontStyle = "#0000FF";

  features;

  constructor() {
    // 丸
    this.trackPointLayer = new WRAP.Geo.Layer(this.TRACK_POINT);
    this.trackPointLayer.setVisible(false);
    // 線
    this.trackLineLayer = new WRAP.Geo.Layer(this.TRACK_LINE);
    this.trackLineLayer.setVisible(false);
    this.trackLineLayer.disableInteraction(true);
  }

  async _initialize() {
    await this._initData();
    this._initLayer();
    this._initHandler();
    this.ready = true;
  }

  _initData() {
    return new Promise(resolve => {
      this.flightHistoryDh = new WRAP.Data(configFlightHistoryDh);
      resolve();
    });
  }

  _initLayer() {
    this.trackPointLayer.configure(this.flightHistoryDh, configFlightHistoryTrackPointLayer);
    this.trackLineLayer.configure(this.flightHistoryDh, configFlightHistoryTrackLineLayer);
  }

  _initHandler() {
    WRAP.Geo.addEventHandler('touch', this._touchHandler.bind(this));
  }

  _touchHandler(layer, feature) {
    if (layer && layer.name() === this.TRACK_POINT) {
      this.setFlightHistoryWindow(feature.geo);
    }
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this._initialize();
    }
    this._setVisible(visible);
  }

  getLayer() {
    return this._getAllLayers();
  }

  _getAllLayers() {
    return [this.trackLineLayer, this.trackPointLayer];
  }
  
  _setVisible(visible) {
    if (visible) {
      this.trackPointLayer.show();
      this.trackLineLayer.show();
    } else {
      this.trackPointLayer.hide();
      this.trackLineLayer.hide();
    }
  }

  _invalidateAllLayer() {
    this.trackPointLayer.invalidate();
    this.trackLineLayer.invalidate();
  }

  updateData(features) {
    const data = deepCopy(features);
    this.features = this._addProperties(data);
    this.flightHistoryDh.update('data', this.features);
  }

  // 描画に必要なプロパティを追加する
  _addProperties(ref) {
    // pointとlineを分別
    const pointFeatures = ref.filter(f => f.geometry.type === 'Point').map(f => {
      f.properties.point = true;
      f.properties.name = formatLonLatATC1(f.geometry.coordinates[0], f.geometry.coordinates[1]);
      return f;
    });

    // LineString(航跡)をFIX間毎に分割する
    const lineStringFeatures = [];
    ref.filter(f => f.geometry.type === 'LineString').forEach(f => {
      const coordinatesList = [];
      f.geometry.coordinates.forEach((currentData, idx) => {

        const nextIdx = idx + 1;
        const nextData = f.geometry.coordinates[nextIdx];
        if (!nextData) return;

        const names = [formatLonLatATC1(currentData[0], currentData[1]), formatLonLatATC1(nextData[0], nextData[1])];
        coordinatesList.push({coordinates: [currentData, nextData], names: names, alt_ft: [f.properties.alt_ft[idx], f.properties.alt_ft[nextIdx]]});
      });

      coordinatesList.forEach((l, idx) => {
        const newFeature = deepCopy(f);
        newFeature.geometry.coordinates = l.coordinates;
        newFeature.properties.gradation = true;
        newFeature.properties.names = l.names;
        newFeature.properties.lineIndex = idx;
        newFeature.properties.alt_ft = l.alt_ft;
        lineStringFeatures.push(newFeature);
      });
    });
    return [...pointFeatures, ...lineStringFeatures];
  }

  setFlightHistoryWindowDispatcher(dispatcher) {
    this.setFlightHistoryWindow = dispatcher;
  }

  getLegend() {
    return {
      id: FlightHistory.NAME,
      label: 'Flight History',
      component: LegendFlightHistory,
      contents: this
    }
  }

  setSources(sources) {
    if (this.features && sources.length) {
      const targets = sources.filter(s => s.visible).map(s => s.dataSourceId);
      const filteredFeature = this.features.filter(f => targets.includes(f.properties.data_source_id));
      this.flightHistoryDh.update('data', filteredFeature);
    }
  }
}

export default FlightHistory;